import { FacilityRadiusPopup } from "@components/map/mapComponents/FacilityRadiusPopup"
import { MapElementMetadata } from "@components/map/utils/utils"
import { AppDispatch } from "@redux/app/store"
import { QueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { Facility } from "api"
import type {
	Circle,
	CircleMarkerOptions,
	GeoJSON,
	LatLng,
	LatLngExpression,
	LayerGroup,
	Map,
	Marker,
	Polygon,
	Polyline,
	PolylineOptions,
} from "leaflet"
import ReactDOM from "react-dom/client"
import { LayerData, mapLayersColors } from "../utils/mapLayers"
import { LayerOptionsWithMetadata } from "../windyMap/windyMap"

type FacilityLayer = Circle | Polygon

const windFacilityDrawPriority: Partial<Record<Facility["type"], number>> = {
	WIND_FARM: 0,
	WIND_SUBSTATION: 1,
	WIND_TURBINE: 2,
}

function generateFacilityRadiusPopupContent(args: {
	facility: Facility
	dispatch?: AppDispatch
	queryClient: QueryClient
	facilityLayer: FacilityLayer
	facilities: Facility[]
	isDipaiUser: boolean
}) {
	const popupNode = document.createElement("div")
	popupNode.id = "popupDiv"
	ReactDOM.createRoot(popupNode).render(
		<FacilityRadiusPopup
			facilities={args.facilities}
			facility={args.facility}
			dispatch={args.isDipaiUser === true ? args.dispatch : undefined}
			queryClient={args.queryClient}
			facilityLayer={args.facilityLayer}
		/>
	)
	return popupNode
}

function normalizeFacilityPolygonCoordinates(
	polygons: Facility["polygons"]
): LatLngExpression[] | LatLngExpression[][] | null {
	if (polygons === undefined || polygons.length === 0) {
		return null
	}

	const normalizedPolygons = polygons
		.map((polygon) =>
			polygon
				.map((point) => {
					if (point.length < 2) {
						return undefined
					}
					const longitude = Number(point[0])
					const latitude = Number(point[1])
					if (
						Number.isFinite(latitude) === false ||
						Number.isFinite(longitude) === false
					) {
						return undefined
					}
					return L.latLng(latitude, longitude)
				})
				.filter((latlng): latlng is LatLng => latlng !== undefined)
		)
		.filter((polygon) => polygon.length >= 3)

	if (normalizedPolygons.length === 0) {
		return null
	}

	if (normalizedPolygons.length === 1) {
		return normalizedPolygons[0] ?? null
	}

	return normalizedPolygons
}

export interface CustomCircleOptions extends CircleMarkerOptions {
	metadata?: MapElementMetadata
}

export const addFacilitiesWithRadius = (
	dispatch: AppDispatch,
	queryClient: QueryClient,
	layerGroup: LayerGroup,
	layerData: LayerData,
	navigate?: ReturnType<typeof useNavigate>,
	map?: Map
) => {
	if (layerGroup === undefined) return

	const { facilitiesRadius } = layerData
	if (facilitiesRadius === undefined) return

	layerGroup.clearLayers()

	const canvasRenderer = L.canvas({ padding: 0.5, pane: "facilitiesRadiusPane" })
	const facilityLayers: FacilityLayer[] = []
	const facilitiesToRender = facilitiesRadius.facilities
		.map((facility, index) => ({
			facility,
			index,
		}))
		.sort((a, b) => {
			const aPriority = windFacilityDrawPriority[a.facility.type] ?? 10
			const bPriority = windFacilityDrawPriority[b.facility.type] ?? 10
			if (aPriority !== bPriority) {
				return aPriority - bPriority
			}
			return a.index - b.index
		})
		.map(({ facility }) => facility)

	for (const facility of facilitiesToRender) {
		const polygonLatLngs = normalizeFacilityPolygonCoordinates(facility.polygons)
		const newFacilityLayer = (
			polygonLatLngs !== null
				? L.polygon(polygonLatLngs, {
						renderer: canvasRenderer,
						color: mapLayersColors.facilities[facility.type],
						fillColor: mapLayersColors.facilities[facility.type],
						fillOpacity: 0.5,
						weight: 2,
						dashArray: [6],
						metadata: {
							facility: {
								name: facility.name,
								type: facility.type,
								radius: facility.radiusM,
							},
						},
					} as PolylineOptions & { metadata?: MapElementMetadata })
				: L.circle([facility.latitude, facility.longitude], {
						renderer: canvasRenderer,
						color: mapLayersColors.facilities[facility.type],
						fillColor: mapLayersColors.facilities[facility.type],
						fillOpacity: 0.5,
						weight: 2,
						dashArray: [6],
						radius: facility.radiusM,
						metadata: {
							facility: {
								name: facility.name,
								type: facility.type,
								radius: facility.radiusM,
							},
						},
					} as CustomCircleOptions)
		) as FacilityLayer

		newFacilityLayer.on("click", function () {
			newFacilityLayer.toggleTooltip()
			const isEditableFacility =
				facility.id >= 0 && facilitiesRadius.company.toLowerCase() === "dipai"
			const popupContent = generateFacilityRadiusPopupContent({
				facility,
				facilities: facilitiesRadius.facilities,
				dispatch: isEditableFacility ? dispatch : undefined,
				queryClient,
				facilityLayer: newFacilityLayer,
				isDipaiUser: isEditableFacility,
			})

			const newPopup = L.popup({
				autoPan: true, // Ensure it pans if necessary
				offset: L.point(0, 0),
				className: "custom-popup", // Add a custom class to target the popup
			})
				.setContent(popupContent)
				.setLatLng(
					"getLatLng" in newFacilityLayer
						? newFacilityLayer.getLatLng()
						: newFacilityLayer.getBounds().getCenter()
				)

			newPopup.on("add", function () {
				requestAnimationFrame(() => {
					const popupWrapper = document.querySelector(
						".custom-popup .leaflet-popup-content-wrapper"
					)

					if (popupWrapper !== null) {
						popupWrapper.classList.remove("leaflet-popup-content-wrapper")
						popupWrapper.classList.add(
							"bg-white",
							"rounded-sm",
							"py-1",
							"px-0",
							"shadow-card"
						)
					}
					newPopup.options.offset = L.point(0, 0)
					newPopup.update()

					map?.eachLayer((layer) => {
						layer.unbindTooltip()
						if ((layer.options as LayerOptionsWithMetadata).metadata !== undefined) {
							layer.fireEvent("mouseout")
						}

						const layerOptionsTyped = layer.options as LayerOptionsWithMetadata
						if (
							layerOptionsTyped?.oldStyle !== undefined &&
							layerOptionsTyped?.fillOpacity !== undefined &&
							layerOptionsTyped.fillOpacity === 0.85
						) {
							if ((layer as unknown as GeoJSON).setStyle !== undefined) {
								;(layer as unknown as GeoJSON).setStyle({
									fillOpacity: layerOptionsTyped?.oldStyle.fillOpacity,
									weight: layerOptionsTyped?.oldStyle.weight,
									color: layerOptionsTyped?.oldStyle.color,
									dashArray: layerOptionsTyped?.oldStyle.dashArray,
									stroke: layerOptionsTyped?.oldStyle.stroke,
								})
							} else if ((layer as unknown as Marker)?.setOpacity !== undefined) {
								;(layer as unknown as Marker).setOpacity(0.5)
							} else if ((layer as unknown as Polyline).setStyle !== undefined) {
								;(layer as unknown as Polyline).setStyle({
									fillOpacity: layerOptionsTyped?.oldStyle.fillOpacity,
									weight: layerOptionsTyped?.oldStyle.weight,
									color: layerOptionsTyped?.oldStyle.color,
									dashArray: layerOptionsTyped?.oldStyle.dashArray,
									stroke: layerOptionsTyped?.oldStyle.stroke,
								})
							}
						}
					})
				})
			})
			newFacilityLayer.bindPopup(newPopup).openPopup()
		})

		facilityLayers.push(newFacilityLayer)
		layerGroup.addLayer(newFacilityLayer)
	}
	layerGroup.addLayer(L.layerGroup(facilityLayers))
}
